import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import './UserDetails.scss'

import backArrow from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/back-arrow-detailed.svg'
import breadcrumbArrow from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/breadcrumb-arrow-detailed.svg' 
import { TransactionXModel } from 'BoostXpressAdmin/Model/transactionXModel'
 
import { CurrencyFormatter, handleSuccess } from 'Helpers'
import { handleError } from 'Helpers/alert/alerts'
import { userXModel } from 'BoostXpressAdmin/Model/userXModel'
import { VirtualAccountXModel } from 'BoostXpressAdmin/Model/virtualAccountXModel'
import { transactionBXService } from 'BoostXpressAdmin/Services/transactionBXService'
import { userBXService } from 'BoostXpressAdmin/Services/userBXService'
import { ModalWrapper } from 'Components'
import { ToastContainer } from 'react-toastify'
import BoostCreditUserDetails from './BoostCreditUserDetails/BoostCreditUserDetails'
import BoostCreditDrawdowns from './BoostCreditDrawdowns/BoostCreditDrawdowns'
import BoostCreditFacilitiesList from './BoostCreditFacilitiesList/BoostCreditFacilitiesList'

type DatasetResponse = {
  data: {
    data: userXModel
  }
}

type ResourceResponse<T> = {
  data: {
    data: T
  }
}

type PagedDatasetResponse<T> = {
  data: {
    data: T[]

    meta: {
      pagination: {
        total: number
        count: number
        per_page: number
        current_page: number
        total_pages: number
        links: {
          next: string
        }
      }
    }
  }
}

const UserDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useState<userXModel | null>(null)
  const [txLoading, setTxLoading] = useState(false)
  const [transactions, setTransactions] = useState<TransactionXModel[]>([])
  const [txTotal, setTxTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)

  const [virtualAccount, setVirtualAccount] = useState<VirtualAccountXModel | null>(null)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [withdrawNote, setWithdrawNote] = useState('')
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditDescription, setCreditDescription] = useState('')
  const [creditReference, setCreditReference] = useState('')
  const [creditSubmitting, setCreditSubmitting] = useState(false)

  const loadUser = async (userId: string) => {
    const res = (await userBXService.show(userId)) as unknown as DatasetResponse
    setUser(res.data.data ?? null)
  }

  const loadTransactions = async (userId: string) => {
    const res = (await transactionBXService.index({
      userResourceId: userId,
      page: pageNumber,
      limit: 100,
      sortOrder: 'DESC',
    })) as unknown as PagedDatasetResponse<TransactionXModel>
    setTransactions(res.data.data ?? [])
    setTxTotal(res.data.meta.pagination.total ?? 0)
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!id) return
      try {
        setIsLoading(true)
        const res = (await userBXService.show(id)) as unknown as DatasetResponse
        if (cancelled) return
        setUser(res.data.data ?? null)
      } catch (e) {
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    setVirtualAccount(null)
  }, [id])

  useEffect(() => {
    let cancelled = false
    const loadTx = async () => {
      if (!id) return
      try {
        setTxLoading(true)
        const res = (await transactionBXService.index({
          userResourceId: id,
          page: pageNumber,
          limit: 100,
          sortOrder: 'DESC',
        })) as unknown as PagedDatasetResponse<TransactionXModel>
        if (cancelled) return
        setTransactions(res.data.data ?? [])
        setTxTotal(res.data.meta.pagination.total ?? 0)
      } catch (e) {
        if (!cancelled) {
          setTransactions([])
          setTxTotal(0)
        }
      } finally {
        if (!cancelled) setTxLoading(false)
      }
    }
    loadTx()
    return () => {
      cancelled = true
    }
  }, [id])

  const fullName = useMemo(() => {
    if (!user) return '-'
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.resourceId
  }, [user])

  const initials = useMemo(() => {
    const parts = fullName.split(' ').filter(Boolean)
    const a = parts[0]?.[0] ?? 'U'
    const b = parts[1]?.[0] ?? ''
    return `${a}${b}`.toUpperCase()
  }, [fullName])

  const statusText = user?.emailVerifiedAt ? 'Email Verified' : 'Email Not Verified'

  const walletBalanceText = user?.wallet
    ? `${user.wallet.currency}${CurrencyFormatter(Number(user.wallet.amountMajor ?? 0))}`
    : '-'
  const totalSpendText = useMemo(() => {
    const out = transactions
      .filter((t) => (t.transactionType ?? '').toLowerCase() === 'out')
      .reduce((sum, t) => sum + Number(t.amountMajor ?? 0), 0)
    return `₦${CurrencyFormatter(out)}`
  }, [transactions])

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString()
  }

  const handleEnableUser = async () => {
    if (!id) return
    setIsLoading(true)

    await userBXService.enable(id).then(
      async () => {
        // Refresh user data
        await loadUser(id)
        setIsLoading(false)
        handleSuccess('User enabled successfully')
      },
      (e) => {
        handleError(e)

        setIsLoading(false)
      },
    )
  }

  const handleDisableUser = async () => {
    if (!id) return
    setIsLoading(true)

    await userBXService.disable(id).then(
      async () => {
        // Refresh user data
        await loadUser(id)
        setIsLoading(false)
        handleSuccess('User disabled successfully')
      },
      (e) => {
        handleError(e)

        setIsLoading(false)
      },
    )
  }

  const openVirtualAccount = async () => {
    if (!id) return

    try {
      const res = (await userBXService.virtualAccount(
        id,
      )) as unknown as ResourceResponse<VirtualAccountXModel>
      setVirtualAccount(res.data.data ?? null)
    } catch (e: any) {
      setVirtualAccount(null)
      handleError({ response: { data: { error: e.message } } })
    }
  }

  useEffect(() => {
    openVirtualAccount()
  }, [id])

  const resetWithdrawModal = () => {
    setShowWithdrawModal(false)
    setWithdrawAmount('')
    setWithdrawNote('')
    setWithdrawSubmitting(false)
  }

  const resetCreditModal = () => {
    setShowCreditModal(false)
    setCreditAmount('')
    setCreditDescription('')
    setCreditReference('')
    setCreditSubmitting(false)
  }

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || withdrawSubmitting) return

    const amountMajor = Number(withdrawAmount)

    if (!amountMajor || Number.isNaN(amountMajor) || amountMajor <= 0) {
      handleError({ response: { data: { error: 'Enter a valid withdrawal amount.' } } })
      return
    }

    if (!withdrawNote.trim()) {
      handleError({ response: { data: { error: 'Withdrawal note is required.' } } })
      return
    }

    setWithdrawSubmitting(true)
    await transactionBXService
      .processWithdrawal({
        userResourceId: id,
        amountMajor,
        note: withdrawNote.trim(),
      })
      .then(
        async () => {
          await Promise.all([loadUser(id), loadTransactions(id)])
          handleSuccess('Withdrawal processed successfully.')
          resetWithdrawModal()
        },
        (error) => {
          handleError(error)
          setWithdrawSubmitting(false)
        },
      )
  }

  const handleCredit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || creditSubmitting) return

    const amountMajor = Number(creditAmount)

    if (!amountMajor || Number.isNaN(amountMajor) || amountMajor <= 0) {
      handleError({ response: { data: { error: 'Enter a valid credit amount.' } } })
      return
    }

    if (!creditDescription.trim()) {
      handleError({ response: { data: { error: 'Description is required.' } } })
      return
    }

    if (!creditReference.trim()) {
      handleError({ response: { data: { error: 'Reference is required.' } } })
      return
    }

    setCreditSubmitting(true)
    await userBXService
      .fundWallet(id, {
        amountMajor,
        description: creditDescription.trim(),
        reference: creditReference.trim(),
      })
      .then(
        async () => {
          await Promise.all([loadUser(id), loadTransactions(id)])
          handleSuccess('Wallet credited successfully.')
          resetCreditModal()
        },
        (error) => {
          handleError(error)
          setCreditSubmitting(false)
        },
      )
  }

  return (
    <div className='bx-user-details-page'>
      <header className='bx-details-header'>
        <div className='bx-breadcrumb-container'>
          <button className='bx-back-btn-wrap' onClick={() => navigate(-1)}>
            <img src={backArrow} alt='Back' className='bx-back-icon-svg' />
          </button>
          <div className='bx-breadcrumb-nav'>
            <Link to='/boostxpress/users' className='bx-breadcrumb-parent'>
              Users Management
            </Link>
            <img src={breadcrumbArrow} alt='' className='bx-breadcrumb-divider' />
            <span className='bx-breadcrumb-active'>User Details</span>
          </div>
        </div>

        <div className='bx-user-profile-summary'>
          <div className='bx-user-avatar-section'>
            <div className='bx-profile-avatar-large'>
              <span className='bx-avatar-initials-text'>{initials}</span>
            </div>
            <div className='bx-user-info'>
              <h2 className='bx-user-display-name'>{isLoading ? 'Loading...' : fullName}</h2>
              <div className='bx-user-meta'>
                <span className='bx-user-id'>{user?.resourceId || '-'}</span>
                <div className='bx-status-pill'>
                  <span className='bx-status-text'>{statusText}</span>
                </div>
              </div>
            </div>
          </div>
          <div className='bx-admin-actions'>
            <button className='bx-action-outline-btn bx-action-outline-btn--reset'>
              Reset Password
            </button>
            {user?.isDisabled === true ? (
              <button
                className='bx-action-outline-btn bx-action-outline-btn--enable'
                disabled={isLoading}
                onClick={handleEnableUser}
              >
                Enable
              </button>
            ) : (
              <button
                className='bx-action-outline-btn bx-action-outline-btn--deactivate'
                disabled={isLoading}
                onClick={handleDisableUser}
              >
                Deactivate
              </button>
            )}
          </div>
        </div>
      </header>

      <div className='bx-details-body'>
        <div className='bx-metrics-grid'>
          <div className='bx-metric-card bx-metric-card--blue'>
            <div className='bx-metric-icon'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M20 7H4C3.44772 7 3 7.44772 3 8V18C3 18.5523 3.44772 19 4 19H20C20.5523 19 21 18.5523 21 18V8C21 7.44772 20.5523 7 20 7Z' stroke='#3378f3' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                <path d='M16 21V5C16 4.44772 15.5523 4 15 4H9C8.44772 4 8 4.44772 8 5V21' stroke='#3378f3' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div className='bx-metric-content'>
              <span className='bx-metric-label-text'>Wallet Balance</span>
              <h3 className='bx-metric-value-text'>{walletBalanceText}</h3>
            </div>
          </div>
          <div className='bx-metric-card bx-metric-card--green'>
            <div className='bx-metric-icon'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z' fill='#2ecc71'/>
              </svg>
            </div>
            <div className='bx-metric-content'>
              <span className='bx-metric-label-text'>Total Spend</span>
              <h3 className='bx-metric-value-text'>{txLoading ? 'Loading...' : totalSpendText}</h3>
            </div>
          </div>
          <div className='bx-metric-card bx-metric-card--orange'>
            <div className='bx-metric-icon'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H9C10.1046 21 11 20.1046 11 19V7C11 5.89543 10.1046 5 9 5Z' stroke='#f0973d' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                <path d='M17 5H15C13.8954 5 13 5.89543 13 7V19C13 20.1046 13.8954 21 15 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5Z' stroke='#f0973d' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div className='bx-metric-content'>
              <span className='bx-metric-label-text'>Transactions</span>
              <h3 className='bx-metric-value-text'>{txLoading ? '-' : txTotal}</h3>
            </div>
          </div>
        </div>

        <div className='bx-action-buttons'>
          <button
            type='button'
            className='bx-action-btn bx-action-btn--withdraw'
            onClick={() => setShowWithdrawModal(true)}
            disabled={isLoading || txLoading}
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 4L13.5 5.5M12 4L10.5 5.5M12 4V15M4 20H20' stroke='#e873a8' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
            Withdraw
          </button>
          <button
            type='button'
            className='bx-action-btn bx-action-btn--credit'
            onClick={() => setShowCreditModal(true)}
            disabled={isLoading || txLoading}
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 4L10.5 5.5M12 4L13.5 5.5M12 4V15M4 20H20' stroke='#00c12b' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
            Credit Wallet
          </button>
        </div>

        <section className='bx-details-section'>
          <h4 className='bx-details-section-title'>Personal Information</h4>
          <div className='bx-info-grid'>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Email Address</span>
              <span className='bx-info-value-text'>{user?.email || '-'}</span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Phone Number</span>
              <span className='bx-info-value-text'>{user?.phone || '-'}</span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Registered</span>
              <span className='bx-info-value-text'>
                {user?.createdAt ? new Date(user.createdAt).toDateString() : '-'}
              </span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Account Provider</span>
              <span className='bx-info-value-text'>
                {virtualAccount?.accountProvider || '-'}
              </span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Bank Name</span>
              <span className='bx-info-value-text'>
                {virtualAccount?.bankName || '-'}
              </span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Account Number</span>
              <span className='bx-info-value-text'>
                {virtualAccount?.bankAccountNumber || '-'}
              </span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Account Name</span>
              <span className='bx-info-value-text'>
                {virtualAccount?.bankAccountName || '-'}
              </span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Expires At</span>
              <span className='bx-info-value-text'>
                {virtualAccount ? formatDateTime(virtualAccount.expiresAt) : '-'}
              </span>
            </div>
          </div>
        </section>

        {user && <BoostCreditUserDetails userResourceId={user.resourceId} />}

        {user && <BoostCreditFacilitiesList userResourceId={user.resourceId} />}

        {user && <BoostCreditDrawdowns userResourceId={user.resourceId} />}

        <section className='bx-details-section'>
          <h4 className='bx-details-section-title'>Transactions History</h4>
          <div className='bx-transactions-history-box'> 
            <div className='bx-history-table-wrapper'>
              <table className='bx-history-table'>
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {txLoading ? (
                    <tr>
                      <td colSpan={7} className='text-center'>
                        Loading...
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className='text-center'>
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((t: TransactionXModel, index) => (
                      <tr key={t.resourceId}>
                        <td>{Number(index) + 1 + (pageNumber - 1) * 100}</td>
                        <td className='bx-amount-cell'>{CurrencyFormatter(Number(t.amountMajor ?? 0))}</td>
                        <td>{t.description}</td>
                        <td>
                          <span className={`bx-status-badge bx-status-badge--${t.paymentStatus.toLowerCase()}`}>
                            {t.paymentStatus}
                          </span>
                        </td>
                        <td>{t.transactionType}</td>
                        <td>{formatDateTime(t.createdAt)}</td>
                        <td>
                          <button
                            className='bx-view-btn'
                            onClick={() => navigate(`/boostxpress/transactions/${t.resourceId}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
      {showWithdrawModal ? (
        <ModalWrapper
          showModal={showWithdrawModal}
          closeModal={resetWithdrawModal}
          title='Process Withdrawal'
          hideHeader={false}
        >
          <form className='bx-withdraw-modal' onSubmit={handleWithdraw}> 
            <div className='alert alert-warning mt-3'>
              <span className='bx-withdraw-modal__summary-label'>Available Wallet Balance</span>
              <strong className='bx-withdraw-modal__summary-value ms-3'>{walletBalanceText}</strong>
            </div>


            <div className='bx-withdraw-modal__field'>
              <label className='bx-withdraw-modal__label'>Amount</label>
              <input
                type='number'
                min='0'
                step='0.01'
                className='form-control'
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder='Enter amount'
                required
              />
            </div>

            <div className='bx-withdraw-modal__field'>
              <label className='bx-withdraw-modal__label'>Note</label>
              <textarea
                className='form-control'
                rows={4}
                value={withdrawNote}
                onChange={(e) => setWithdrawNote(e.target.value)}
                placeholder='Enter withdrawal note'
                required
              />
            </div>

            <div className='bx-withdraw-modal__actions'>
              <button type='submit' className='btn btn-default' disabled={withdrawSubmitting}>
                {withdrawSubmitting ? 'Processing...' : 'Process Withdrawal'}
              </button>
            </div>
          </form>
        </ModalWrapper>
      ) : null}

      {showCreditModal ? (
        <ModalWrapper
          showModal={showCreditModal}
          closeModal={resetCreditModal}
          title='Credit User Wallet'
          hideHeader={false}
        >
          <form className='bx-credit-modal' onSubmit={handleCredit}>
            <div className='alert alert-warning mt-3'>
              <span className='bx-credit-modal__summary-label'>Current Wallet Balance</span>
              <strong className='bx-credit-modal__summary-value ms-3'>{walletBalanceText}</strong>
            </div>

            <div className='bx-credit-modal__field'>
              <label className='bx-credit-modal__label'>Amount</label>
              <input
                type='number'
                min='0'
                step='0.01'
                className='form-control'
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder='Enter amount'
                required
              />
            </div>

            <div className='bx-credit-modal__field'>
              <label className='bx-credit-modal__label'>Description</label>
              <textarea
                className='form-control'
                rows={4}
                value={creditDescription}
                onChange={(e) => setCreditDescription(e.target.value)}
                placeholder='Enter description'
                required
              />
            </div>

            <div className='bx-credit-modal__field'>
              <label className='bx-credit-modal__label'>Reference</label>
              <input
                type='text'
                className='form-control'
                value={creditReference}
                onChange={(e) => setCreditReference(e.target.value)}
                placeholder='Enter reference'
                required
              />
            </div>

            <div className='bx-credit-modal__actions'>
              <button type='submit' className='btn btn-default' disabled={creditSubmitting}>
                {creditSubmitting ? 'Processing...' : 'Credit Wallet'}
              </button>
            </div>
          </form>
        </ModalWrapper>
      ) : null}
      <ToastContainer></ToastContainer>
    </div>
  )
}

export default UserDetails
